<template>
  <command-palette v-if="isHelloDataEmployee" />
  <q-layout v-if="user" class="window-height" container view="lHh Lpr lff">
    <q-header :class="{ 'bg-dark': $q.dark.isActive, 'bg-white text-black': !$q.dark.isActive }">
      <teleport-target id="toolbar-portal" />
    </q-header>
    <q-drawer
      :mini="isMini"
      :model-value="true"
      :width="230"
      behavior="desktop"
      bordered
      class="column no-wrap"
      no-mini-animation
      side="left"
    >
      <q-list>
        <q-item class="q-mt-sm q-mb-lg">
          <router-link to="/">
            <q-item-section v-if="!isMini">
              <img
                v-if="$q.dark.isActive"
                id="hd-logo"
                alt="HelloData Logo"
                src="/hd-logo-white.svg"
                width="200px"
              />
              <img v-else id="hd-logo" alt="HelloData Logo" src="/hd-logo.svg" width="200px" />
            </q-item-section>
            <q-item-section v-else avatar>
              <q-img height="20px" no-spinner src="/hd-wave-logo.svg" width="20px" />
            </q-item-section>
          </router-link>
        </q-item>
        <div class="intercom-drawer-actions">
          <template v-if="hasPropertyReports">
            <q-item v-if="!isMini" dense>
              <address-autocomplete
                :show-surfaced-reports-only="!canCreatePropertyReports"
                dense
                placeholder="Quick Report..."
                surface-reports
                @new-address="openOverview($event.id)"
              />
            </q-item>
            <q-item v-else clickable dense>
              <q-item-section avatar>
                <q-avatar icon="search" />
              </q-item-section>
              <q-tooltip v-if="isMini" anchor="center right"> Quick Report </q-tooltip>
              <q-menu transition-duration="100">
                <q-banner>
                  <address-autocomplete
                    dense
                    placeholder="Quick Report..."
                    surface-reports
                    @new-address="openOverview($event.id)"
                  />
                </q-banner>
              </q-menu>
            </q-item>
            <!-- active class must be blank otherwise it'll be purple on the new report page -->
            <q-item
              v-if="canCreatePropertyReports"
              :to="{ name: 'New Property Report' }"
              clickable
              dense
              @click="$tracker.clickedNewReportButton('sidebar')"
            >
              <q-item-section avatar>
                <q-avatar icon="add_circle_outline" size="35px" />
              </q-item-section>
              <q-tooltip v-if="isMini" anchor="center right"> New Report </q-tooltip>
              <q-item-section> New Report </q-item-section>
            </q-item>
            <q-item
              v-if="canCreateNewDevelopments"
              :to="{ name: 'New Development' }"
              active-class="text-primary indented-primary"
              clickable
              dense
            >
              <q-item-section avatar>
                <q-avatar icon="domain_add" size="35px" />
              </q-item-section>
              <q-tooltip v-if="isMini" anchor="center right"> New Development </q-tooltip>
              <q-item-section> New Development </q-item-section>
            </q-item>
          </template>
          <q-item-label class="menu-header q-mt-md" header>
            <q-item-section> Navigation </q-item-section>
          </q-item-label>
          <q-item
            id="intercom-dashboard"
            :to="{ name: 'Dashboard' }"
            active-class="text-primary indented-primary"
            clickable
            dense
          >
            <q-item-section avatar>
              <q-avatar icon="o_space_dashboard" size="35px" />
            </q-item-section>
            <q-tooltip v-if="isMini" anchor="center right"> Dashboard </q-tooltip>
            <q-item-section>Dashboard</q-item-section>
          </q-item>
          <q-item
            v-if="hasPropertyReports && canCreateMarketAnalytics"
            id="intercom-market-analytics"
            :to="{ name: 'Market Analytics' }"
            active-class="text-primary indented-primary"
            clickable
            dense
          >
            <q-item-section avatar>
              <q-avatar icon="o_map" size="35px" />
            </q-item-section>
            <q-tooltip v-if="isMini" anchor="center right"> Market Analytics </q-tooltip>
            <q-item-section>Market Analytics</q-item-section>
          </q-item>
          <q-item
            v-if="hasPropertyReports && canCreateQueryBuilderDatasets"
            id="intercom-dataset-builder"
            :to="{ name: 'Datasets' }"
            active-class="text-primary indented-primary"
            clickable
            dense
          >
            <q-item-section avatar>
              <q-avatar icon="data_usage" size="35px" />
            </q-item-section>
            <q-tooltip v-if="isMini" anchor="center right"> Query Builder </q-tooltip>
            <q-item-section> Query Builder </q-item-section>
          </q-item>
          <q-item
            v-if="hasMarketFinderFeature"
            id="intercom-market-finder"
            :to="{ name: 'Similar Markets' }"
            active-class="text-primary indented-primary"
            clickable
            dense
          >
            <q-item-section avatar>
              <q-avatar icon="o_travel_explore" size="35px" />
            </q-item-section>
            <q-tooltip v-if="isMini" anchor="center right"> Market Finder </q-tooltip>
            <q-item-section> Market Finder </q-item-section>
          </q-item>
          <q-item
            v-if="hasRentRollParserFeature"
            id="intercom-rent-roll-parser"
            :to="{ name: 'Rent Roll Parser' }"
            active-class="text-primary indented-primary"
            clickable
            dense
          >
            <q-item-section avatar>
              <q-avatar icon="o_description" size="35px" />
            </q-item-section>
            <q-tooltip v-if="isMini" anchor="center right"> Rent Roll Parser </q-tooltip>
            <q-item-section>
              <div>
                Rent Roll Parser
                <q-badge class="q-ml-sm">Beta</q-badge>
              </div>
            </q-item-section>
          </q-item>
          <q-item
            id="intercom-recent-visits"
            clickable
            dense
            @mouseenter="openRecentVisitsMenu"
            @mouseleave="queueCloseRecentVisitsMenu"
          >
            <q-item-section avatar>
              <q-avatar icon="o_history" size="35px" />
            </q-item-section>
            <q-tooltip v-if="isMini" anchor="center right"> Recent Visits </q-tooltip>
            <q-item-section>Recent Visits</q-item-section>
            <q-item-section side>
              <q-icon name="chevron_right" size="18px" />
            </q-item-section>
            <q-menu
              v-model="recentVisitsMenuOpen"
              anchor="top right"
              self="top left"
              :offset="[8, 0]"
              transition-duration="100"
              @mouseenter="openRecentVisitsMenu"
              @mouseleave="queueCloseRecentVisitsMenu"
            >
              <q-list style="min-width: 360px; max-width: 420px">
                <q-item-label header>Recent Visits</q-item-label>
                <q-item v-if="recentVisitItems.length === 0" dense>
                  <div class="text-grey-6 q-px-md q-pb-md">No recent visits yet</div>
                </q-item>
                <q-item
                  v-for="visit in recentVisitItems"
                  :key="`${visit.report_type}:${visit.report_id}`"
                  dense
                  clickable
                  v-close-popup
                  :to="visit.route"
                >
                  <q-item-section avatar>
                    <q-icon :name="visit.icon" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="ellipsis">{{ visit.name }}</q-item-label>
                    <q-item-label caption>{{ visit.typeLabel }}</q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-item-label caption>{{ visit.lastVisitedLabel }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-item>
        </div>
        <template v-if="isNotSubscribed">
          <q-item-label class="menu-header q-mt-md" header>
            <q-item-section> Sample Reports </q-item-section>
          </q-item-label>
          <q-item
            v-for="sample in propertyReportSamples"
            :key="sample.name"
            :to="{ name: sample.name }"
            active-class="text-accent indented-accent"
            clickable
            dense
          >
            <q-item-section>
              <q-item-label>
                <div class="ellipsis">{{ sample.name }}</div>
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
      <q-space />
      <q-item dense>
        <q-item-section>
          <q-chip v-if="propertyReportsPastDue" :ripple="false" class="text-deep-orange" outline>
            <div class="text-center full-width">Past Due</div>
            <q-tooltip>Your subscription is past due.</q-tooltip>
          </q-chip>
        </q-item-section>
      </q-item>
      <q-item clickable dense @click="toggleDrawer">
        <q-item-section avatar>
          <q-avatar v-if="!isMini" icon="arrow_back" size="35px" />
          <q-avatar v-else icon="arrow_forward" />
        </q-item-section>
        <q-tooltip v-if="isMini" anchor="center right"> Open Sidebar </q-tooltip>
        <q-item-section>Close Sidebar</q-item-section>
      </q-item>
      <q-item v-if="isBackgroundTasksVisible" :to="{ name: 'Background Tasks' }" clickable dense>
        <q-item-section avatar>
          <q-spinner v-if="queuedPendingTasks" size="17.5px" />
          <q-avatar v-else icon="o_manage_history" size="35px" />
          <q-badge
            v-if="showUnreadBackgroundTasksNotification"
            class="background-task-notification"
            color="primary"
            left
            rounded
          />
        </q-item-section>
        <q-tooltip v-if="isMini" anchor="center right"> Background Tasks </q-tooltip>
        <q-item-section>Background Tasks</q-item-section>
      </q-item>
      <q-item id="intercom-scheduled-reports" :to="{ name: 'Scheduled Emails' }" clickable dense>
        <q-item-section avatar>
          <q-avatar icon="o_schedule" size="35px" />
        </q-item-section>
        <q-tooltip v-if="isMini" anchor="center right"> Scheduled Emails </q-tooltip>
        <q-item-section>Scheduled Emails</q-item-section>
      </q-item>
      <q-item id="intercom-organization" :to="{ name: 'Organization' }" clickable dense>
        <q-item-section avatar>
          <q-avatar icon="business" size="35px" />
        </q-item-section>
        <q-tooltip v-if="isMini" anchor="center right"> Organization </q-tooltip>
        <q-item-section>Organization</q-item-section>
      </q-item>
      <q-item id="intercom-support-feedback" clickable dense @click="openIntercom">
        <q-item-section avatar>
          <q-avatar icon="support_agent" size="35px" />
        </q-item-section>
        <q-tooltip v-if="isMini" anchor="center right"> Support & Feedback </q-tooltip>
        <q-item-section>
          <div>
            Support & Feedback
            <q-badge v-if="intercomCount > 0" :label="intercomCount" class="q-ml-xs" color="red" />
          </div>
        </q-item-section>
      </q-item>
      <q-list id="intercom-account">
        <q-separator />
        <q-item clickable>
          <q-item-section avatar top>
            <q-avatar round size="28px">
              <q-img
                :src="user.picture"
                alt="User Picture"
                no-spinner
                referrerpolicy="no-referrer"
                width="28px"
              />
            </q-avatar>
          </q-item-section>
          <q-item-section class="ellipsis">
            <q-item-label v-if="userName">{{ userName }}</q-item-label>
            <q-item-label v-else>{{ user.email }}</q-item-label>
            <q-item-label caption>
              {{ accountSubtitle }}
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-icon name="more_vert" />
          </q-item-section>
          <q-menu transition-duration="100">
            <q-list>
              <q-item id="intercom-profile" :to="{ name: 'Profile' }" clickable>
                <q-item-section avatar>
                  <q-icon name="person_outline" />
                </q-item-section>
                <q-item-section>Profile</q-item-section>
              </q-item>
              <q-item
                v-if="isHelloDataEmployee"
                id="intercom-admin"
                :to="{ name: 'Admin' }"
                clickable
              >
                <q-item-section avatar>
                  <q-icon name="o_admin_panel_settings" />
                </q-item-section>
                <q-item-section>Admin</q-item-section>
              </q-item>
              <q-item
                v-if="isAdmin"
                id="intercom-invite-people"
                :to="{ name: 'Organization' }"
                clickable
              >
                <q-item-section avatar>
                  <q-icon name="mail_outline" />
                </q-item-section>
                <q-item-section> Invite Users </q-item-section>
              </q-item>
              <q-item
                id="intercom-billing"
                :disable="!hasBillingPortalUrl"
                :to="{ name: 'Billing' }"
                clickable
              >
                <q-item-section avatar>
                  <q-icon name="o_account_balance" />
                </q-item-section>
                <q-item-section> Billing </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                href="https://www.hellodata.ai/learning-hub"
                target="_blank"
              >
                <q-item-section avatar>
                  <q-icon name="help_outline" />
                </q-item-section>
                <q-item-section>
                  <div class="flex">
                    <div>Help Center</div>
                    <q-icon class="q-ml-sm q-my-auto" name="launch" />
                  </div>
                </q-item-section>
              </q-item>
              <q-item v-close-popup clickable @click="hardRefreshToDashboard">
                <q-item-section avatar>
                  <q-icon name="refresh" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>Update Platform</q-item-label>
                  <q-item-label caption>{{ versionDate }}</q-item-label>
                </q-item-section>
              </q-item>
              <q-item clickable @click="toggleDarkMode">
                <q-item-section avatar>
                  <q-icon :name="Dark.isActive ? 'o_light_mode' : 'o_dark_mode'" />
                </q-item-section>
                <q-item-section>{{ Dark.isActive ? 'Light' : 'Dark' }} Mode</q-item-section>
              </q-item>
              <q-item v-close-popup clickable>
                <q-item-section avatar>
                  <q-icon name="logout" />
                </q-item-section>
                <q-item-section class="text-red" @click="signOut"> Sign Out </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-item>
      </q-list>
    </q-drawer>

    <teleport-target id="right-drawer-portal" />

    <q-page-container :class="{ 'bg-grey-10': $q.dark.isActive }" class="full-height bg-grey-2">
      <q-page
        id="app-layout-scroll-area"
        :class="{ 'q-pb-xl': !isMarketAnalysisEditOrNewRoute }"
        :padding="!isMarketAnalysisEditOrNewRoute"
      >
        <router-view></router-view>
      </q-page>
    </q-page-container>
  </q-layout>
  <div v-else class="window-height">
    <q-inner-loading :showing="!user" color="secondary" />
  </div>
</template>

<script lang="ts">
import { RouterView } from 'vue-router'
import { Dialog, Dark } from 'quasar'
import LogRocket from 'logrocket'
import { formatDistance, parseISO } from 'date-fns'

import { useUserStore } from '@/stores/user'
import { useStripeStore } from '@/stores/stripe'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { useMarketAnalysisStore } from '@/stores/marketanalytics'
import { useDatasetsStore } from '@/stores/datasets'
import { useRentRollStore } from '@/stores/rentroll'
import AddressAutocomplete from '@/components/PropertyReports/AddressAutocomplete.vue'
import slackApi from '@/api/slack'
import type { OrganizationAttributes } from 'common'
import { propertyReportSamples } from 'common'
import useBackgroundTasksStore from '@/stores/backgroundtask'
import { isLoaded } from '@/utils/loading'
import CommandPalette from '@/components/CommandPalette.vue'

declare const Intercom: (action: string, ...args: any[]) => void

export default {
  name: 'AppView',
  components: { CommandPalette, RouterView, AddressAutocomplete },
  data() {
    return {
      Dark,
      mounted: false,
      isMini: false,
      intercomCount: 0,
      recentVisitsMenuOpen: false,
      recentVisitsMenuCloseTimeout: null as ReturnType<typeof setTimeout> | null,
      handleMonitoringKeyboardShortcut: null as ((e: KeyboardEvent) => void) | null,
      propertyReportSamples
    }
  },
  computed: {
    isBackgroundTasksVisible(): boolean {
      const user = useUserStore().user
      if (user === undefined) {
        return false
      }

      return useUserStore().isHelloDataEmployee && user.jobTitle === 'Other'
    },
    versionDate(): string {
      const ts = (import.meta.env.VITE_COMMIT_TIMESTAMP as string) || null
      if (!ts) return 'local build'
      const asNumber = Number(ts)
      const date = isNaN(asNumber) ? new Date(ts) : new Date(asNumber)
      if (isNaN(date.getTime())) return 'local build'
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    },
    isMarketAnalysisEditOrNewRoute() {
      return (
        this.$route.name === 'New Market Analytics' ||
        this.$route.name === 'Market Analysis' ||
        this.$route.name === 'Chicago Market Analytics'
      )
    },
    isNotSubscribed() {
      return !this.hasPropertyReports && !this.hasLimitedAccess
    },
    hasMarketFinderFeature() {
      return useStripeStore().hasMarketFinder
    },
    hasQueryBuilderFeature() {
      return useStripeStore().hasQueryBuilder
    },
    isHelloDataEmployee() {
      return useUserStore().isHelloDataEmployee
    },
    mobileWidth() {
      return this.$q.screen.lt.md
    },
    recentVisitItems() {
      const propertyNames = new Map(
        usePropertyReportStore().activeSubscriptions.map((_) => [_.id, _.name])
      )
      const marketNames = new Map(
        useMarketAnalysisStore().activeSubscriptions.map((_) => [_.id, _.name])
      )

      const datasetNames = new Map<number, string>()
      const datasets = useDatasetsStore().datasets
      if (isLoaded(datasets)) {
        for (const dataset of datasets.data) {
          datasetNames.set(dataset.id, dataset.name)
        }
      }

      const rentRollNames = new Map<number, string>()
      const rentRolls = useRentRollStore().savedRentRolls
      if (isLoaded(rentRolls)) {
        for (const rentRoll of rentRolls.data) {
          rentRollNames.set(rentRoll.id, rentRoll.name)
        }
      }

      return useUserStore()
        .allKnownReportHistory.slice(0, 10)
        .map((visit) => {
          let resolvedName = visit.report_name || `Report ${visit.report_id}`
          if (visit.report_type === 'property_report') {
            resolvedName = propertyNames.get(visit.report_id) || resolvedName
          } else if (visit.report_type === 'market_analysis') {
            resolvedName = marketNames.get(visit.report_id) || resolvedName
          } else if (visit.report_type === 'dataset') {
            resolvedName = datasetNames.get(visit.report_id) || resolvedName
          } else if (visit.report_type === 'rent_roll') {
            resolvedName = rentRollNames.get(visit.report_id) || resolvedName
          }

          return {
            ...visit,
            name: resolvedName,
            typeLabel: this.visitTypeLabel(visit.report_type),
            icon: this.visitTypeIcon(visit.report_type),
            route: this.visitRoute(visit.report_type, visit.report_id),
            lastVisitedLabel: this.lastVisitedLabel(visit.last_visited_at)
          }
        })
    },
    accountSubtitle(): string {
      return useUserStore().organization?.display_name || 'Account Settings'
    },
    userName(): string | undefined {
      const user = this.user
      if (user && user?.given_name && user?.family_name) {
        return `${user.given_name} ${user.family_name}`
      }
      return user?.email
    },
    user() {
      return this.$auth0.user.value
    },
    hasLimitedAccess(): boolean {
      return useStripeStore().hasLimitedAccess
    },
    hasPropertyReports(): boolean {
      return useStripeStore().hasPropertyReports
    },
    hasMarketReports(): boolean {
      return useStripeStore().hasMarketReports
    },
    hasRentRollParserFeature() {
      return useStripeStore().hasRentRollParser
    },
    hasBillingPortalUrl() {
      return useStripeStore().hasCustomerPortal
    },
    organizationInvitation() {
      return useUserStore().organizationInvitation
    },
    propertyReportsPastDue() {
      if (!useUserStore().isAdmin) {
        // Only admin/owners should see this
        return false
      }
      const sub = useStripeStore().subscriptions.find((_) =>
        _.products.some(
          (p: { product_id: string }) =>
            p.product_id === import.meta.env.VITE_STRIPE_PROPERTY_REPORTS_PRODUCT
        )
      )
      return sub?.past_due || null
    },
    organization() {
      return useUserStore().organization
    },
    isAdmin() {
      return useUserStore().isAdmin
    },
    canCreatePropertyReports() {
      return useUserStore().canCreatePropertyReports
    },
    canCreateNewDevelopments() {
      return useUserStore().canCreateNewDevelopments
    },
    canCreateMarketAnalytics() {
      return useUserStore().canCreateMarketAnalytics
    },
    canCreateQueryBuilderDatasets() {
      return useUserStore().canCreateQueryBuilderDatasets
    },
    backgroundTasks() {
      return useBackgroundTasksStore().backgroundTasks
    },
    queuedPendingTasks() {
      const store = useBackgroundTasksStore()
      return store.queued.length > 0 || store.pending.length > 0
    },
    showUnreadBackgroundTasksNotification() {
      return this.backgroundTasks.some(
        (_) => (_.status === 'complete' || _.status === 'failure') && _.finished_read_on === null
      )
    }
  },
  watch: {
    isMarketAnalysisEditOrNewRoute: {
      handler(newValue) {
        if (newValue) {
          this.isMini = true
        } else {
          this.isMini = this.mobileWidth
        }
      },
      immediate: true
    },
    mobileWidth() {
      if (!this.isMarketAnalysisEditOrNewRoute) {
        this.updateDrawer()
      }
    },
    organizationInvitation() {
      if (this.organizationInvitation) {
        Dialog.create({
          title: 'Organization Invitation',
          message: `You have been invited to join <b>${this.organizationInvitation.display_name}</b>. Would you like to accept?`,
          persistent: true,
          html: true, // technically, we should escape the message
          ok: {
            label: 'Accept',
            color: 'primary',
            unelevated: true,
            noCaps: true
          },
          cancel: {
            label: 'Deny',
            color: 'negative',
            unelevated: true,
            noCaps: true
          }
        })
          .onOk(() => {
            this.acceptInvitation()
          })
          .onCancel(() => {
            this.denyInvitation()
          })
      }
    }
  },
  created() {
    const {
      initOrganizationMembers,
      initOrganization,
      initInvitation,
      initPeopleInOrgOrShared,
      initReportHistory,
      initWebAuth,
      initHelpSoftware
    } = useUserStore()

    const { initCustomerSubscriptions, getCustomerPortal, initPropertyReportTrialConsumed } =
      useStripeStore()

    initPeopleInOrgOrShared().catch((err) => {
      console.error('Error initializing people in org or shared', err)
    })
    initReportHistory().catch((err) => {
      console.error('Error initializing report history', err)
    })
    initWebAuth()

    Promise.all([initOrganization(), initCustomerSubscriptions(), initOrganizationMembers()])
      .then(([org]: [OrganizationAttributes | null, void, void]) => {
        initInvitation().catch((err) => {
          console.error('Error initializing people in org or shared', err)
        })
        initPropertyReportTrialConsumed().catch((err) => {
          console.error('Error initializing people in org or shared', err)
        })

        // for now, we need to get both the org data and what subscriptions the user has
        if (this.user && this.user.email) {
          this.$tracker.signedIn(this.user, org?.display_name, org?.id)
        }
        getCustomerPortal().catch((err) => {
          console.error('Error initializing people in org or shared', err)
        })
        initHelpSoftware()
        Intercom('onUnreadCountChange', (unreadCount: number) => {
          this.intercomCount = unreadCount
        })
      })
      .catch((err) => {
        console.error('Error initializing people in org or shared', err)
      })

    this.updateDrawer()
  },
  mounted() {
    if (this.isBackgroundTasksVisible) {
      // Poll the tasks store. This will check to see if there are any pending or
      // queued tasks. The background tasks endpoint will continue to be polled
      // until all of the tasks are resolved (i.e. complete or failed).
      useBackgroundTasksStore()
        .poll()
        .catch((e) => {
          console.error('Error polling background tasks', e)
        })
    }

    if (this.isMarketAnalysisEditOrNewRoute) {
      this.isMini = true
    }
    // monitor clicks on the SHIFT + UP key press, for internalOnly users
    if (this.isHelloDataEmployee) {
      this.handleMonitoringKeyboardShortcut = (e) => {
        if (e.key === 'ArrowUp' && e.shiftKey) {
          const date = new Date()
          const formattedDate = date.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })
          const sessionId = LogRocket.sessionURL?.split('/').pop() || 'unknown'

          const fields = [
            {
              type: 'mrkdwn',
              text: `*User:*\n${this.user?.email}`
            },
            {
              type: 'mrkdwn',
              text: `*Time:*\n${formattedDate}`
            },
            {
              type: 'mrkdwn',
              text: `*Action:*\nPressed SHIFT + UP key`
            },
            {
              type: 'mrkdwn',
              text: `*Location:*\n${window.location.href}`
            },
            {
              type: 'mrkdwn',
              text: `*LogRocket Session:*\n${LogRocket.sessionURL ? `<${LogRocket.sessionURL}|${sessionId}>` : 'No session URL'}`
            }
          ]

          if (
            window.location.pathname.startsWith('/property-reports/') &&
            !window.location.pathname.includes('/edit') &&
            !window.location.pathname.includes('/print')
          ) {
            fields.push({
              type: 'mrkdwn',
              text: `*Copy Report:*\n<${window.location.origin}/settings/admin/copy-report/${window.location.pathname.split('/').pop()}|Copy this report>`
            })
          } else if (
            window.location.pathname.startsWith('/property-reports/') &&
            !window.location.pathname.includes('/print')
          ) {
            fields.push({
              type: 'mrkdwn',
              text: `*Copy Report:*\n<${window.location.origin}/settings/admin/copy-report/${window.location.pathname.split('/')[2]}|Copy this report>`
            })
          }

          const msg = {
            blocks: [
              {
                type: 'header',
                text: {
                  type: 'plain_text',
                  text: '🔍 User Activity Monitor',
                  emoji: true
                }
              },
              {
                type: 'section',
                fields: fields
              }
            ]
          }

          LogRocket.error(JSON.stringify(msg))
          slackApi.sendStructuredMessage(msg.blocks).catch(console.error)
        }
      }
      window.addEventListener('keydown', this.handleMonitoringKeyboardShortcut)
    }
  },
  unmounted() {
    if (this.recentVisitsMenuCloseTimeout) {
      clearTimeout(this.recentVisitsMenuCloseTimeout)
      this.recentVisitsMenuCloseTimeout = null
    }
    if (this.handleMonitoringKeyboardShortcut) {
      window.removeEventListener('keydown', this.handleMonitoringKeyboardShortcut)
    }
  },
  methods: {
    openRecentVisitsMenu() {
      if (this.recentVisitsMenuCloseTimeout) {
        clearTimeout(this.recentVisitsMenuCloseTimeout)
        this.recentVisitsMenuCloseTimeout = null
      }
      this.recentVisitsMenuOpen = true
    },
    queueCloseRecentVisitsMenu() {
      if (this.recentVisitsMenuCloseTimeout) {
        clearTimeout(this.recentVisitsMenuCloseTimeout)
      }
      this.recentVisitsMenuCloseTimeout = setTimeout(() => {
        this.recentVisitsMenuOpen = false
        this.recentVisitsMenuCloseTimeout = null
      }, 120)
    },
    visitTypeLabel(type: 'property_report' | 'market_analysis' | 'dataset' | 'rent_roll') {
      if (type === 'property_report') return 'Property Report'
      if (type === 'market_analysis') return 'Market Analysis'
      if (type === 'dataset') return 'Query Builder'
      return 'Rent Roll'
    },
    visitTypeIcon(type: 'property_report' | 'market_analysis' | 'dataset' | 'rent_roll') {
      if (type === 'property_report') return 'o_space_dashboard'
      if (type === 'market_analysis') return 'o_map'
      if (type === 'dataset') return 'data_usage'
      return 'o_description'
    },
    visitRoute(type: 'property_report' | 'market_analysis' | 'dataset' | 'rent_roll', id: number) {
      if (type === 'property_report') {
        return { name: 'Property Report', params: { reportId: String(id) } }
      }
      if (type === 'market_analysis') {
        return { name: 'Market Analysis', params: { reportId: String(id) } }
      }
      if (type === 'dataset') {
        return { name: 'Dataset Viewer', params: { datasetId: String(id) } }
      }
      return { name: 'View Rent Roll', params: { rentRollId: String(id) } }
    },
    lastVisitedLabel(lastVisitedAt: string) {
      const timestamp = parseISO(lastVisitedAt)
      if (Number.isNaN(timestamp.getTime())) {
        return '-'
      }
      return formatDistance(timestamp, new Date(), { addSuffix: true })
    },
    toggleDrawer() {
      const currentlyMini = this.isMini
      this.$tracker.toggledSidebar(!currentlyMini)
      this.isMini = !currentlyMini
    },
    updateDrawer() {
      this.isMini = this.mobileWidth
    },
    openOverview(id: string) {
      this.$router.push({ name: 'New Property Report', query: { propertyId: id } })
    },
    openIntercom() {
      this.$tracker.openedIntercom()
      // add a copy of the current page to the intercom custom attributes
      const custom_attributes: Record<string, string> = {
        'Current Page': window.location.pathname,
        'Job Type': this.user?.jobTitle
      }
      if (
        window.location.pathname.startsWith('/property-reports/') &&
        !window.location.pathname.includes('/edit') &&
        !window.location.pathname.includes('/print')
      ) {
        const reportId = window.location.pathname.split('/').pop() || ''
        custom_attributes['Report ID'] = reportId
        custom_attributes['Copy Report'] =
          `${window.location.origin}/settings/admin/copy-report/${reportId}`
      } else if (
        window.location.pathname.startsWith('/property-reports/') &&
        !window.location.pathname.includes('/print')
      ) {
        const reportId = window.location.pathname.split('/')[2] || ''
        custom_attributes['Report ID'] = reportId
        custom_attributes['Copy Report'] =
          `${window.location.origin}/settings/admin/copy-report/${reportId}`
      }
      Intercom('update', { ...custom_attributes })
      Intercom('show')
    },
    signOut() {
      this.$tracker.signedOut()
      this.$auth0.logout({ logoutParams: { returnTo: 'https://www.hellodata.ai' } })
    },
    hardRefreshToDashboard() {
      // Resolve the Dashboard route to ensure we land on the right path
      const resolved = this.$router.resolve({ name: 'Dashboard' })
      const href = resolved.href || '/'
      const sep = href.includes('?') ? '&' : '?'
      // Add a cache-busting query param so the HTML is not loaded from cache
      const target = `${href}${sep}hard_refresh=${Date.now()}`
      // Use replace so the old page isn't in the back stack
      window.location.replace(target)
    },
    async denyInvitation() {
      await useUserStore()
        .denyInvitation()
        .then(() => {
          this.$tracker.receivedInvitation(false)
        })
    },
    async acceptInvitation() {
      await useUserStore()
        .acceptInvitation()
        .then(() => {
          this.$tracker.receivedInvitation(true)
          this.$auth0.getAccessTokenSilently({ cacheMode: 'off' }).then(() => {
            useUserStore()
              .initOrganizationMembers()
              .catch((err) => {
                console.error('Error initializing organization members', err)
              })
            useUserStore()
              .initOrganization()
              .catch((err) => {
                console.error('Error initializing organization', err)
              })
            useUserStore()
              .initPeopleInOrgOrShared()
              .catch((err) => {
                console.error('Error initializing people in org or shared', err)
              })
            useUserStore().initWebAuth()
            useStripeStore()
              .initCustomerSubscriptions()
              .catch((err) => {
                console.error(err)
              })
          })
        })
    },
    toggleDarkMode() {
      localStorage.setItem('prefer-dark', (!Dark.isActive).toString())
      Dark.toggle()
    }
  }
}
</script>

<style lang="scss" scoped>
.q-avatar {
  width: inherit;
}
.q-item__section--avatar {
  min-width: inherit;
}
.menu-header {
  font-size: 13px;
  padding-bottom: 8px;
}
.indented-accent {
  box-shadow: 4 0 0 -2px $accent inset;
}
.indented-primary {
  box-shadow: 4 0 0 -2px $primary inset;
}

.background-task-notification {
  position: absolute;
  top: -4px;
  left: 3px;
  cursor: inherit;
}
</style>
